import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Checkbox, FormControlLabel, FormGroup } from '@mui/material'

import { LiveEventEvolutionTypeEnum } from '../../../../../features/live-events/types/LiveEventCollection'
import { getLiveEventEvolutionTypeTranslation } from '../../../../../features/live-events/utils/utils'

interface EventEvolutionTypesSelectorProps {
  selectedTypes: LiveEventEvolutionTypeEnum[] | undefined
  onTypesChange: (types: LiveEventEvolutionTypeEnum[]) => void
  disabled?: boolean
}

// Available types excluding HIGHLIGHTED as per the original logic
const AVAILABLE_TYPES = [LiveEventEvolutionTypeEnum.Reskin, LiveEventEvolutionTypeEnum.MechanicChange] as const

export const EventEvolutionTypesSelector: React.FC<EventEvolutionTypesSelectorProps> = ({ selectedTypes, onTypesChange, disabled = false }) => {
  const { t } = useTranslation()

  // Memoize the selected types set for O(1) lookup performance
  const selectedTypesSet = useMemo(() => {
    return new Set(selectedTypes || [])
  }, [selectedTypes])

  // Optimize the change handler with useCallback to prevent unnecessary re-renders
  const handleTypeChange = useCallback(
    (type: LiveEventEvolutionTypeEnum, checked: boolean) => {
      const currentTypes = selectedTypes || []
      if (checked) {
        onTypesChange([...currentTypes, type])
      } else {
        onTypesChange(currentTypes.filter((t) => t !== type))
      }
    },
    [selectedTypes, onTypesChange]
  )

  // Memoize the checkbox change handler to prevent recreation on every render
  const createCheckboxChangeHandler = useCallback(
    (type: LiveEventEvolutionTypeEnum) => {
      return (event: React.ChangeEvent<HTMLInputElement>) => {
        handleTypeChange(type, event.target.checked)
      }
    },
    [handleTypeChange]
  )

  return (
    <FormGroup
      sx={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 1,
      }}
      role="group"
      aria-label={t('live-events:evolution_types_selector_label')}
    >
      {AVAILABLE_TYPES.map((type) => (
        <FormControlLabel
          disableTypography
          key={type}
          control={
            <Checkbox
              checked={selectedTypesSet.has(type)}
              size="small"
              onChange={createCheckboxChangeHandler(type)}
              disabled={disabled}
              sx={{ py: 0, alignSelf: 'flex-start' }}
              inputProps={{
                'aria-label': t(getLiveEventEvolutionTypeTranslation(type) || ''),
              }}
            />
          }
          label={t(getLiveEventEvolutionTypeTranslation(type) || '')}
        />
      ))}
    </FormGroup>
  )
}
